import React from "react";
import { 
  FiX, 
  FiCheckCircle, 
  FiClock, 
  FiMapPin, 
  FiUser, 
  FiTag, 
  FiDollarSign, 
  FiCalendar,
  FiImage,
  FiVideo,
  FiEye,
  FiDownload,
  FiShare2,
  FiAward,
  FiTrendingUp,
  FiFileText,
  FiType,
  FiHash,
  FiCalendar as FiCalendarIcon,
  FiClock as FiClockIcon
} from "react-icons/fi";
import { 
  MdOutlineClose, 
  MdOutlineCheckCircle,
  MdOutlineAccessTime,
  MdOutlineLocationOn,
  MdOutlinePerson,
  MdOutlineAttachMoney,
  MdOutlineCalendarToday,
  MdOutlineImage,
  MdOutlineVideoLibrary,
  MdOutlineCategory,
  MdOutlineVerified,
  MdOutlineCancel,
  MdOutlinePending,
  MdOutlineLocalOffer,
  MdOutlineCampaign
} from "react-icons/md";
import { 
  IoCloseOutline, 
  IoCheckmarkCircle,
  IoTimeOutline,
  IoLocationOutline,
  IoPersonOutline,
  IoCashOutline,
  IoCalendarOutline,
  IoImageOutline,
  IoVideocamOutline,
  IoPricetagOutline
} from "react-icons/io5";

const ViewAdvertisement = ({ onClose, data }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusConfig = (status) => {
    const config = {
      approved: { 
        color: "text-emerald-700 bg-emerald-50 border-emerald-200", 
        icon: FiCheckCircle,
        label: "Approved"
      },
      pending: { 
        color: "text-amber-700 bg-amber-50 border-amber-200", 
        icon: FiClock,
        label: "Pending"
      },
      rejected: { 
        color: "text-rose-700 bg-rose-50 border-rose-200", 
        icon: FiX,
        label: "Rejected"
      },
      active: { 
        color: "text-emerald-700 bg-emerald-50 border-emerald-200", 
        icon: FiCheckCircle,
        label: "Active"
      },
      expired: { 
        color: "text-gray-700 bg-gray-50 border-gray-200", 
        icon: FiClock,
        label: "Expired"
      }
    };
    return config[status] || config.pending;
  };

  const StatusBadge = ({ status }) => {
    const config = getStatusConfig(status);
    const Icon = config.icon;
    return (
      <span className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
        ${config.color}
      `}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  const InfoRow = ({ icon: Icon, label, value, color = "text-gray-500" }) => (
    <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100 hover:border-[#715EFE]/30 transition-all duration-200">
      <span className={`mt-0.5 ${color} shrink-0`}>
        {Icon && <Icon className="w-4 h-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
          {label}
        </p>
        <p className="text-sm text-gray-700 font-medium break-words">
          {value ?? "—"}
        </p>
      </div>
    </div>
  );

  const Section = ({ title, icon: Icon, children }) => (
    <div className="bg-gray-50/80 rounded-xl border border-gray-200 p-4 space-y-3 hover:bg-gray-50 transition-colors duration-200">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#715EFE]" />}
        <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400">
          {title}
        </h4>
      </div>
      {children}
    </div>
  );

  if (!data) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-in fade-in duration-200">
        <div className="bg-white rounded-2xl shadow-2xl p-10 text-center max-w-sm w-full animate-in zoom-in-95 duration-200">
          <div className="p-4 bg-rose-50 rounded-full w-fit mx-auto mb-4">
            <FiX className="text-rose-400 text-4xl" />
          </div>
          <p className="text-gray-600 font-medium">No advertisement data available.</p>
          <button
            onClick={onClose}
            className="mt-5 px-6 py-2.5 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-700 transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(data?.status);
  const StatusIcon = statusConfig.icon;
  const isVideo =
    data?.mediaType === "video" ||
    (data?.imageUrl &&
      (data.imageUrl.includes(".mp4") || data.imageUrl.includes(".mov"))) ||
    !!data?.videoUrl;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl relative flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#855DF6] via-[#7B5CF6] to-[#715EFE] px-6 py-5 relative flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <MdOutlineCampaign className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Advertisement Details</h2>
                <p className="text-xs text-white/70 hidden sm:block">
                  View advertisement information
                </p>
              </div>
            </div>
            <button
              className="p-2 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-all duration-200"
              onClick={onClose}
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
          
          {/* Status Badge */}
          <div className="mt-3">
            <StatusBadge status={data?.status} />
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pt-5 pb-6 space-y-4 flex-1 overflow-y-auto">
          {/* Title */}
          <div className="flex items-start gap-3">
            <div className="p-2 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-lg">
              <FiTag className="w-4 h-4 text-[#715EFE]" />
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Title</p>
              <h3 className="text-xl font-bold text-gray-800">{data?.title || "Untitled"}</h3>
            </div>
          </div>

          {/* Media */}
          {(data?.imageUrl || data?.videoUrl) && (
            <div className="rounded-xl overflow-hidden shadow-md border border-gray-200 bg-gray-100">
              {isVideo ? (
                <video
                  src={data?.videoUrl || data?.imageUrl}
                  poster={data?.imageUrl}
                  controls
                  className="w-full h-56 object-cover bg-black"
                />
              ) : (
                <img
                  src={data?.imageUrl}
                  alt={data?.title || "Advertisement"}
                  className="w-full h-56 object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
            </div>
          )}

          {/* User Info */}
          {data?.type !== "Admin" && data?.user && (
            <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 hover:bg-gray-100 transition-colors duration-200">
              <img
                src={data?.user?.profileImage || "https://via.placeholder.com/60"}
                alt={data?.user?.fullName}
                className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-md"
                onError={(e) => {
                  e.target.style.background = "#e2e8f0";
                  e.target.src = "";
                }}
              />
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                  Requested by
                </p>
                <p className="text-sm font-semibold text-gray-700">
                  {data?.user?.fullName || "—"}
                </p>
              </div>
              {data?.user?.createdAt && (
                <p className="ml-auto text-xs text-gray-400">
                  Joined {formatDate(data.user.createdAt)}
                </p>
              )}
            </div>
          )}

          {/* Plan Details */}
          {data?.planId && (
            <Section title="Plan Information" icon={FiDollarSign}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <InfoRow
                  icon={FiTag}
                  label="Plan Name"
                  value={data?.planId?.title || "—"}
                />
                <InfoRow
                  icon={FiDollarSign}
                  label="Price"
                  value={`$${data?.planId?.price || 0}`}
                  color="text-emerald-500"
                />
                <InfoRow
                  icon={FiClock}
                  label="Duration"
                  value={`${data?.planId?.durationInDays || 0} day(s)`}
                />
              </div>
            </Section>
          )}

          {/* Dates */}
          <Section title="Timeline" icon={FiCalendar}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data?.approveAt && (
                <InfoRow
                  icon={FiCheckCircle}
                  label="Approved At"
                  value={formatDate(data?.approveAt)}
                  color="text-emerald-500"
                />
              )}
              {(data?.startDate || data?.approveAt) && (
                <InfoRow
                  icon={FiCalendar}
                  label="Start Date"
                  value={formatDate(data?.startDate || data?.approveAt)}
                />
              )}
              {data?.expireAt && (
                <InfoRow
                  icon={FiClock}
                  label="Expires At"
                  value={formatDate(data?.expireAt)}
                  color="text-rose-500"
                />
              )}
              {(data?.endDate || data?.expireAt) && (
                <InfoRow
                  icon={FiCalendar}
                  label="End Date"
                  value={formatDate(data?.endDate || data?.expireAt)}
                />
              )}
            </div>
          </Section>

          {/* Placement Positions */}
          {data?.positions && data?.positions?.length > 0 && (
            <Section title="Placement Positions" icon={FiMapPin}>
              <div className="flex flex-wrap gap-2">
                {data.positions.map((position, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium border border-purple-200 hover:bg-purple-100 transition-colors duration-200"
                  >
                    <FiMapPin className="w-3 h-3" />
                    <span className="capitalize">{position.replace("-", " ")}</span>
                    {data?.placementConfig?.[position] && (
                      <span className="ml-1 text-purple-500 text-[10px]">
                        ({data.placementConfig[position].replaceAll("_", " ")})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Additional Info */}
          <Section title="Additional Info" icon={FiFileText}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoRow
                icon={FiType}
                label="Type"
                value={data?.type || "—"}
              />
              <InfoRow
                icon={FiFileText}
                label="Media Type"
                value={data?.mediaType || "—"}
              />
              <InfoRow
                icon={FiCalendar}
                label="Created"
                value={formatDate(data?.createdAt)}
              />
              <InfoRow
                icon={FiClock}
                label="Last Updated"
                value={formatDate(data?.updatedAt)}
              />
            </div>
          </Section>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/50">
          <button
            className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewAdvertisement;